/**
 * Shared Contacts Components
 * Used by both Platform and Agency Contacts pages
 * GoHighLevel-style CRM contacts interface
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Users,
  Search,
  Filter,
  Plus,
  Upload,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Phone,
  Mail,
  RefreshCw,
  ArrowUpDown,
  X,
  Settings2,
  Tag,
  Calendar,
  Clock,
  UserPlus,
  Trash2,
  Edit,
  Eye,
  Download,
  Zap,
  MessageSquare,
  Send,
  Building2,
  Merge,
  CheckCircle,
  Ban,
  Star,
  StarOff,
  XCircle,
  ListFilter,
  Check,
  Loader2,
  User,
  Globe,
  CreditCard,
  Activity,
  Hash,
  FileText,
  DollarSign,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================
// FILTER FIELD CATEGORIES (GHL-Style)
// ============================================
export const FILTER_CATEGORIES = [
  {
    id: 'contact_information',
    name: 'Contact information',
    fields: [
      { key: 'first_name', label: 'First name', type: 'text' },
      { key: 'last_name', label: 'Last name', type: 'text' },
      { key: 'full_name', label: 'Full name', type: 'text' },
      { key: 'email', label: 'Email', type: 'text' },
      { key: 'email_status', label: 'Email status', type: 'select', options: ['valid', 'invalid', 'unverified'] },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'business_name', label: 'Business name', type: 'text' },
      { key: 'company_name', label: 'Company name', type: 'text' },
      { key: 'contact_source', label: 'Contact source', type: 'select' },
      { key: 'contact_type', label: 'Contact type', type: 'select', options: ['lead', 'prospect', 'client', 'former_client', 'self_repair'] },
      { key: 'status', label: 'Status', type: 'select', options: ['active', 'inactive', 'pending', 'do_not_contact', 'archived'] },
      { key: 'country', label: 'Country', type: 'text' },
      { key: 'city', label: 'City', type: 'text' },
      { key: 'state', label: 'State', type: 'text' },
      { key: 'postal_zip_code', label: 'Postal zip code', type: 'text' },
      { key: 'street_address', label: 'Street Address', type: 'text' },
      { key: 'created_at', label: 'Created', type: 'date' },
      { key: 'created_by', label: 'Created by', type: 'text' },
      { key: 'date_of_birth', label: 'Date of birth', type: 'date' },
      { key: 'owner', label: 'Owner', type: 'select' },
      { key: 'tag', label: 'Tag', type: 'tag' },
      { key: 'timezone', label: 'Timezone', type: 'text' },
      { key: 'website', label: 'Website', type: 'text' },
    ],
  },
  {
    id: 'dnd',
    name: 'DND',
    fields: [
      { key: 'dnd_calls', label: 'Calls & Voicemails DND', type: 'boolean' },
      { key: 'dnd_all', label: 'DND all', type: 'boolean' },
      { key: 'dnd_email', label: 'Email DND', type: 'boolean' },
      { key: 'dnd_sms', label: 'SMS DND', type: 'boolean' },
      { key: 'dnd_whatsapp', label: 'WhatsApp DND', type: 'boolean' },
      { key: 'dnd_inbound', label: 'Inbound DND', type: 'boolean' },
    ],
  },
  {
    id: 'contact_activity',
    name: 'Contact activity',
    fields: [
      { key: 'active_campaign', label: 'Active campaign', type: 'select' },
      { key: 'campaign_status', label: 'Campaign status', type: 'select', options: ['active', 'paused', 'finished', 'canceled'] },
      { key: 'import', label: 'Import', type: 'select' },
      { key: 'last_activity', label: 'Last activity', type: 'date' },
      { key: 'last_activity_type', label: 'Last activity type', type: 'select' },
      { key: 'last_appointment', label: 'Last appointment', type: 'date' },
      { key: 'updated_at', label: 'Updated', type: 'date' },
      { key: 'workflow_active', label: 'Workflow active', type: 'boolean' },
      { key: 'workflow_finished', label: 'Workflow finished', type: 'boolean' },
    ],
  },
  {
    id: 'opportunity_information',
    name: 'Opportunity information',
    fields: [
      { key: 'opportunity_pipeline', label: 'Opportunity pipeline', type: 'select' },
      { key: 'opportunity_stage', label: 'Opportunity stage', type: 'select' },
      { key: 'opportunity_status', label: 'Opportunity status', type: 'select', options: ['open', 'won', 'lost', 'abandoned'] },
    ],
  },
  {
    id: 'client_portal',
    name: 'Client portal',
    fields: [
      { key: 'portal_access', label: 'Portal access', type: 'boolean' },
      { key: 'portal_last_login', label: 'Portal last login', type: 'date' },
      { key: 'portal_groups', label: 'Groups', type: 'select' },
    ],
  },
  {
    id: 'attribution',
    name: 'Attribution',
    fields: [
      { key: 'last_attribution', label: 'Last attribution', type: 'date' },
      { key: 'attribution_fb_click_id', label: 'Attribution FB click ID', type: 'text' },
      { key: 'attribution_google_click_id', label: 'Attribution Google click ID', type: 'text' },
      { key: 'attribution_medium', label: 'Attribution medium', type: 'text' },
      { key: 'attribution_source', label: 'Attribution source', type: 'text' },
      { key: 'attribution_campaign', label: 'Attribution campaign', type: 'text' },
    ],
  },
  {
    id: 'engagement',
    name: 'Contact engagement score',
    fields: [
      { key: 'engagement_score', label: 'Engagement Score', type: 'number' },
    ],
  },
];

// Filter operators by type
export const FILTER_OPERATORS = {
  text: [
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' },
    { value: 'contains', label: 'contains' },
    { value: 'does_not_contain', label: 'does not contain' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'ends_with', label: 'ends with' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  date: [
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' },
    { value: 'before', label: 'before' },
    { value: 'after', label: 'after' },
    { value: 'between', label: 'between' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  number: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'not equals' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'less_than', label: 'less than' },
    { value: 'between', label: 'between' },
  ],
  select: [
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  boolean: [
    { value: 'is_true', label: 'is true' },
    { value: 'is_false', label: 'is false' },
  ],
  tag: [
    { value: 'has', label: 'has' },
    { value: 'does_not_have', label: 'does not have' },
    { value: 'has_any', label: 'has any' },
    { value: 'has_none', label: 'has none' },
  ],
};

// ============================================
// BULK ACTION BAR COMPONENT
// ============================================
export const BulkActionBar = ({ 
  selectedCount, 
  totalCount, 
  onSelectAll, 
  onDeselectAll, 
  onExport,
  onTriggerAutomation,
  onSendEmail,
  onAddTags,
  onRemoveTags,
  onDelete,
  onMore,
  scope = 'agency',
}) => {
  const [showMore, setShowMore] = useState(false);

  const moreActions = [
    { id: 'send_sms', label: 'Send SMS', icon: MessageSquare, enabled: true },
    { id: 'send_email', label: 'Send email', icon: Mail, enabled: true },
    { id: 'send_whatsapp', label: 'Send WhatsApp', icon: MessageSquare, enabled: false, reason: 'WhatsApp integration not configured' },
    { id: 'request_reviews', label: 'Request reviews', icon: Star, enabled: false, reason: 'Reviews module not enabled' },
    { id: 'manage_companies', label: 'Manage companies', icon: Building2, enabled: true },
    { id: 'manage_opportunities', label: 'Manage opportunities', icon: DollarSign, enabled: true },
    { id: 'trigger_automation', label: 'Trigger automation', icon: Zap, enabled: true },
    { id: 'add_tags', label: 'Add tags', icon: Tag, enabled: true },
    { id: 'remove_tags', label: 'Remove tags', icon: Tag, enabled: true },
    { id: 'export', label: 'Export', icon: Download, enabled: true },
    { id: 'merge', label: 'Merge', icon: Merge, enabled: selectedCount === 2 },
    { id: 'verify_emails', label: 'Verify email addresses', icon: CheckCircle, enabled: true },
    { id: 'delete', label: 'Delete', icon: Trash2, enabled: true, danger: true },
  ];

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="bulk-action-bar">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-blue-700">
          {selectedCount} Contact{selectedCount !== 1 ? 's' : ''} Selected
        </span>
        {selectedCount < totalCount && (
          <button 
            onClick={onSelectAll}
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Select all {totalCount}
          </button>
        )}
      </div>
      
      <div className="flex-1" />
      
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onExport} className="text-slate-700 hover:bg-slate-100">
          <Download className="w-4 h-4 mr-1.5" />
          Export
        </Button>
        <Button variant="ghost" size="sm" onClick={onTriggerAutomation} className="text-slate-700 hover:bg-slate-100">
          <Zap className="w-4 h-4 mr-1.5" />
          Trigger automation
        </Button>
        <Button variant="ghost" size="sm" onClick={onSendEmail} className="text-slate-700 hover:bg-slate-100">
          <Mail className="w-4 h-4 mr-1.5" />
          Send email
        </Button>
        <Button variant="ghost" size="sm" onClick={onAddTags} className="text-slate-700 hover:bg-slate-100">
          <Tag className="w-4 h-4 mr-1.5" />
          Add tags
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-600 hover:bg-red-50">
          <Trash2 className="w-4 h-4 mr-1.5" />
          Delete
        </Button>
        
        {/* More Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="text-slate-700 hover:bg-slate-100">
              More
              <ChevronDown className="w-4 h-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            {moreActions.map((action) => {
              const Icon = action.icon;
              return (
                <DropdownMenuItem
                  key={action.id}
                  onClick={() => action.enabled && onMore?.(action.id)}
                  disabled={!action.enabled}
                  className={cn(
                    action.danger && 'text-red-600 focus:text-red-600',
                    !action.enabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  <div className="flex-1">
                    <span>{action.label}</span>
                    {!action.enabled && action.reason && (
                      <p className="text-xs text-slate-400 mt-0.5">{action.reason}</p>
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <button onClick={onDeselectAll} className="p-1 hover:bg-blue-100 rounded">
        <X className="w-4 h-4 text-blue-600" />
      </button>
    </div>
  );
};

// ============================================
// ADD TO AUTOMATION MODAL
// ============================================
export const AddToAutomationModal = ({ 
  isOpen, 
  onClose, 
  selectedContacts = [], 
  token,
  scope = 'agency',
  agencyId,
}) => {
  const [actionName, setActionName] = useState('');
  const [automationType, setAutomationType] = useState('workflow');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [mode, setMode] = useState('send_all');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [batchSize, setBatchSize] = useState(100);
  const [interval, setInterval] = useState(1);
  const [intervalUnit, setIntervalUnit] = useState('hours');
  
  const [workflows, setWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [workflowPickerOpen, setWorkflowPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch workflows when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchWorkflows();
    }
  }, [isOpen, scope, agencyId]);

  const fetchWorkflows = async () => {
    setLoadingWorkflows(true);
    try {
      let endpoint = scope === 'platform' 
        ? `${API}/api/platform/system-automations/workflows?status=active`
        : `${API}/api/agencies/${agencyId}/automations/workflows?status=published`;
      
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWorkflows(res.data.workflows || res.data || []);
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
      toast.error('Failed to load workflows');
    } finally {
      setLoadingWorkflows(false);
    }
  };

  const filteredWorkflows = useMemo(() => {
    if (!searchQuery) return workflows;
    return workflows.filter(w => 
      w.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      w.category?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [workflows, searchQuery]);

  const handleSubmit = async () => {
    if (!actionName || !selectedWorkflow) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        action_name: actionName,
        automation_type: automationType,
        workflow_id: selectedWorkflow.id,
        contact_ids: selectedContacts.map(c => c.id || c),
        mode,
        scheduled_date: mode === 'scheduled' ? scheduledDate : null,
        scheduled_time: mode === 'scheduled' ? scheduledTime : null,
        batch_size: mode === 'drip' ? batchSize : null,
        interval: mode === 'drip' ? interval : null,
        interval_unit: mode === 'drip' ? intervalUnit : null,
      };

      const endpoint = scope === 'platform'
        ? `${API}/api/platform/contacts/bulk-actions/trigger-automation`
        : `${API}/api/agencies/${agencyId}/contacts/bulk-actions/trigger-automation`;

      await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Automation "${selectedWorkflow.name}" triggered for ${selectedContacts.length} contact(s)`);
      onClose();
    } catch (err) {
      console.error('Failed to trigger automation:', err);
      toast.error('Failed to trigger automation');
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = actionName && selectedWorkflow;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="add-to-automation-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">Add to automation</DialogTitle>
          <DialogDescription>
            Runs the selected workflow or campaign for all selected contacts
          </DialogDescription>
        </DialogHeader>

        {/* Selected Contacts Preview */}
        <div className="flex items-center gap-2 py-3 border-b">
          <div className="flex -space-x-2">
            {selectedContacts.slice(0, 5).map((contact, i) => (
              <div 
                key={i}
                className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-medium border-2 border-white"
              >
                {contact.first_name?.[0]}{contact.last_name?.[0] || '?'}
              </div>
            ))}
            {selectedContacts.length > 5 && (
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-medium border-2 border-white">
                +{selectedContacts.length - 5}
              </div>
            )}
          </div>
          <span className="text-sm text-slate-600">
            {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
          </span>
        </div>

        <div className="space-y-4 py-4">
          {/* Action Name */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Action name *</Label>
            <Input
              placeholder="Write a name for the action (to be shown in tracking report)"
              value={actionName}
              onChange={(e) => setActionName(e.target.value)}
            />
          </div>

          {/* Automation Type */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Automation type *</Label>
            <Select value={automationType} onValueChange={setAutomationType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="workflow">Workflow</SelectItem>
                <SelectItem value="campaign" disabled>Campaign (coming soon)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Workflow Selector */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Workflow *</Label>
            <Popover open={workflowPickerOpen} onOpenChange={setWorkflowPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between font-normal"
                  data-testid="workflow-dropdown"
                >
                  {selectedWorkflow ? (
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-purple-500" />
                      <span>{selectedWorkflow.name}</span>
                      {selectedWorkflow.status && (
                        <Badge variant="outline" className="text-xs">
                          {selectedWorkflow.status}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-500">Select a workflow...</span>
                  )}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput 
                    placeholder="Search workflows..." 
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                  />
                  <CommandList className="max-h-[300px] overflow-y-auto">
                    {loadingWorkflows ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                      </div>
                    ) : filteredWorkflows.length === 0 ? (
                      <CommandEmpty>No workflows found</CommandEmpty>
                    ) : (
                      <CommandGroup>
                        {filteredWorkflows.map((workflow) => (
                          <CommandItem
                            key={workflow.id}
                            value={workflow.id}
                            onSelect={() => {
                              setSelectedWorkflow(workflow);
                              setWorkflowPickerOpen(false);
                              setSearchQuery('');
                            }}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <Check className={cn(
                              "w-4 h-4",
                              selectedWorkflow?.id === workflow.id ? "opacity-100" : "opacity-0"
                            )} />
                            <Zap className="w-4 h-4 text-purple-500" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{workflow.name}</p>
                              {workflow.category && (
                                <p className="text-xs text-slate-500">{workflow.category}</p>
                              )}
                            </div>
                            <Badge variant="outline" className="text-xs shrink-0">
                              {workflow.status || 'active'}
                            </Badge>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Mode Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Mode</Label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="mode"
                  value="send_all"
                  checked={mode === 'send_all'}
                  onChange={(e) => setMode(e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm">Send all at once</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="mode"
                  value="scheduled"
                  checked={mode === 'scheduled'}
                  onChange={(e) => setMode(e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm">Send at scheduled time</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="mode"
                  value="drip"
                  checked={mode === 'drip'}
                  onChange={(e) => setMode(e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm">Send in drip mode</span>
              </label>
            </div>
          </div>

          {/* Scheduled Time Options */}
          {mode === 'scheduled' && (
            <div className="grid grid-cols-2 gap-3 pl-6">
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Time</Label>
                <Input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Drip Mode Options */}
          {mode === 'drip' && (
            <div className="grid grid-cols-3 gap-3 pl-6">
              <div className="space-y-1">
                <Label className="text-xs">Batch size</Label>
                <Input
                  type="number"
                  value={batchSize}
                  onChange={(e) => setBatchSize(parseInt(e.target.value) || 100)}
                  min={1}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Interval</Label>
                <Input
                  type="number"
                  value={interval}
                  onChange={(e) => setInterval(parseInt(e.target.value) || 1)}
                  min={1}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Unit</Label>
                <Select value={intervalUnit} onValueChange={setIntervalUnit}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutes">Minutes</SelectItem>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex gap-2">
              <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700">
                Please note the action will be performed over period of time. You can track the progress on the bulk actions page.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!isValid || submitting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              'Add to automation'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// ADVANCED FILTERS SIDE PANEL
// ============================================
export const AdvancedFiltersPanel = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onClear,
  customFields = [],
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(['contact_information']);
  const [activeFilters, setActiveFilters] = useState(filters || []);

  const allCategories = useMemo(() => {
    const cats = [...FILTER_CATEGORIES];
    if (customFields.length > 0) {
      cats.push({
        id: 'custom_fields',
        name: 'Custom fields',
        fields: customFields.map(f => ({
          key: `custom_${f.id}`,
          label: f.name,
          type: f.field_type || 'text',
        })),
      });
    }
    return cats;
  }, [customFields]);

  const filteredCategories = useMemo(() => {
    if (!searchQuery) return allCategories;
    return allCategories.map(cat => ({
      ...cat,
      fields: cat.fields.filter(f => 
        f.label.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    })).filter(cat => cat.fields.length > 0);
  }, [allCategories, searchQuery]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const addFilter = (field) => {
    const newFilter = {
      id: Date.now(),
      field: field.key,
      label: field.label,
      type: field.type,
      operator: FILTER_OPERATORS[field.type]?.[0]?.value || 'is',
      value: '',
    };
    setActiveFilters(prev => [...prev, newFilter]);
  };

  const updateFilter = (filterId, updates) => {
    setActiveFilters(prev => 
      prev.map(f => f.id === filterId ? { ...f, ...updates } : f)
    );
  };

  const removeFilter = (filterId) => {
    setActiveFilters(prev => prev.filter(f => f.id !== filterId));
  };

  const handleApply = () => {
    onFiltersChange(activeFilters);
    onApply(activeFilters);
  };

  const handleClear = () => {
    setActiveFilters([]);
    onClear();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div 
        className="fixed right-0 top-0 h-full w-[480px] bg-white shadow-xl z-50 flex flex-col"
        data-testid="advanced-filters-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-slate-900">Advanced Filters</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search field"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="px-6 py-3 border-b bg-blue-50">
            <p className="text-xs font-medium text-blue-700 mb-2">Active filters ({activeFilters.length})</p>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {activeFilters.map((filter) => (
                <div key={filter.id} className="flex items-center gap-2 p-2 bg-white rounded border">
                  <span className="text-sm font-medium text-slate-700 min-w-[100px]">{filter.label}</span>
                  <Select 
                    value={filter.operator} 
                    onValueChange={(v) => updateFilter(filter.id, { operator: v })}
                  >
                    <SelectTrigger className="h-8 w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FILTER_OPERATORS[filter.type]?.map(op => (
                        <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(filter.operator) && (
                    <Input
                      value={filter.value}
                      onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                      className="h-8 flex-1"
                      placeholder="Value..."
                    />
                  )}
                  <button 
                    onClick={() => removeFilter(filter.id)}
                    className="p-1 hover:bg-slate-100 rounded"
                  >
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter Categories */}
        <div className="flex-1 overflow-y-auto">
          {filteredCategories.map((category) => (
            <div key={category.id} className="border-b">
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center gap-2 px-6 py-3 hover:bg-slate-50 transition-colors"
              >
                <ChevronDown 
                  className={cn(
                    "w-4 h-4 text-slate-400 transition-transform",
                    !expandedCategories.includes(category.id) && "-rotate-90"
                  )}
                />
                <span className="text-sm font-medium text-slate-700">{category.name}</span>
              </button>
              
              {expandedCategories.includes(category.id) && (
                <div className="pb-2">
                  {category.fields.map((field) => (
                    <button
                      key={field.key}
                      onClick={() => addFilter(field)}
                      className="w-full flex items-center justify-between px-10 py-2 text-sm text-slate-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      <span>{field.label}</span>
                      <Plus className="w-4 h-4 opacity-0 group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-slate-50">
          <Button variant="ghost" onClick={handleClear} className="text-slate-600">
            Clear all filters
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleApply} className="bg-blue-600 hover:bg-blue-700">
              Apply
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================
// SMART LIST TABS COMPONENT
// ============================================
export const SmartListTabs = ({
  smartLists = [],
  selectedList,
  onSelectList,
  onAddList,
  totalContacts = 0,
}) => {
  const [showMore, setShowMore] = useState(false);
  const visibleLists = smartLists.slice(0, 5);
  const hiddenLists = smartLists.slice(5);

  return (
    <div className="flex items-center gap-1 px-2 py-2 border-b bg-slate-50" data-testid="smart-list-tabs">
      {/* All Contacts Tab */}
      <button
        onClick={() => onSelectList(null)}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
          !selectedList
            ? 'bg-white text-blue-700 shadow-sm border border-blue-200'
            : 'text-slate-600 hover:bg-white hover:shadow-sm'
        )}
        data-testid="smart-list-all"
      >
        <Users className="w-4 h-4" />
        All
        <Badge variant="secondary" className="ml-1 text-xs">
          {totalContacts}
        </Badge>
      </button>

      {/* Smart List Tabs */}
      {visibleLists.map((list) => (
        <button
          key={list.id}
          onClick={() => onSelectList(list.id)}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            selectedList === list.id
              ? 'bg-white text-blue-700 shadow-sm border border-blue-200'
              : 'text-slate-600 hover:bg-white hover:shadow-sm'
          )}
          data-testid={`smart-list-${list.id}`}
        >
          <span 
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: list.color || '#6366f1' }}
          />
          <span className="truncate max-w-[120px]">{list.name}</span>
          <Badge variant="secondary" className="ml-1 text-xs">
            {list.contact_count || 0}
          </Badge>
        </button>
      ))}

      {/* More Dropdown */}
      {hiddenLists.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="text-slate-600">
              {hiddenLists.length} more
              <ChevronDown className="w-4 h-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {hiddenLists.map((list) => (
              <DropdownMenuItem
                key={list.id}
                onClick={() => onSelectList(list.id)}
                className="flex items-center gap-2"
              >
                <span 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: list.color || '#6366f1' }}
                />
                <span>{list.name}</span>
                <Badge variant="secondary" className="ml-auto text-xs">
                  {list.contact_count || 0}
                </Badge>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Add Smart List */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={onAddList}
        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add smart list
      </Button>
    </div>
  );
};

export default {
  BulkActionBar,
  AddToAutomationModal,
  AdvancedFiltersPanel,
  SmartListTabs,
  FILTER_CATEGORIES,
  FILTER_OPERATORS,
};
